/**
 * T1 of the optimization program: gives the service layer request identity.
 * See `docs/2026-09-03-plan-optimizacion-requests-renders.md`.
 *
 * Two mechanisms, and the difference matters when deciding which to use:
 *
 *   inflight(key, fn)        Collapses concurrent identical calls into one.
 *                            Nothing is retained, so it can never serve a stale
 *                            value. Safe on anything, including balances.
 *
 *   cached(key, fn, ttl)     Also retains the answer for ttl milliseconds.
 *                            This is where staleness lives, so every caller
 *                            picks a TTL from the table below deliberately.
 *
 * Failures are never retained: a rejected call leaves no entry, so the next
 * caller retries instead of inheriting the error for the rest of the TTL.
 */

/** TTLs agreed with the business on 2026-09-03. */
export const CacheTTL = {
  /**
   * Until the page reloads or invalidate() is called. For catalogs that change
   * on deploy, not during a working day.
   */
  SESSION: Number.POSITIVE_INFINITY,
  /**
   * Services and payment modes an admin can disable mid-day, e.g. when a bank
   * goes down. Five minutes is how long a disabled option may keep showing.
   */
  LOCKS: 5 * 60 * 1000,
  /** Vehicles and drivers: a newly added one appears within this window. */
  FLEET: 15 * 60 * 1000,
  /**
   * Today's exchange rate. It moves up to three times a day (09:00, 13:00,
   * 17:00) and must be current shortly after each change.
   */
  RATE_TODAY: 5 * 60 * 1000,
  /**
   * A past date's exchange rate is immutable: the rate that applied on a given
   * day never changes, so it is worth retaining for the whole session.
   */
  RATE_PAST: Number.POSITIVE_INFINITY,
} as const;

interface CacheEntry {
  value: unknown;
  /** Epoch ms after which the entry is stale. Infinity means session-long. */
  expiresAt: number;
}

const entries = new Map<string, CacheEntry>();
const pending = new Map<string, Promise<unknown>>();

/**
 * Bumped by every invalidate(). A request captures it before awaiting and only
 * writes its result if it still matches, so a response that was already in
 * flight when the store or the user changed cannot repopulate the cache it was
 * meant to be cleared from. Without this, logging out while a request started
 * by the previous screen is still open silently reinserts that context's data.
 */
let epoch = 0;

/**
 * Collapses concurrent calls that share a key into a single request. Retains
 * nothing, so it never serves a stale value.
 */
export const inflight = <T,>(key: string, fn: () => Promise<T>): Promise<T> => {
  const running = pending.get(key);
  if (running) return running as Promise<T>;

  const promise = fn().finally(() => {
    pending.delete(key);
  });

  pending.set(key, promise);
  return promise;
};

/**
 * inflight plus retention for ttlMs. A ttl of 0 or less bypasses retention and
 * degrades to plain deduplication.
 *
 * shouldRetain decides whether a resolved value is worth keeping. It matters
 * because most services in this repo swallow their errors and resolve with an
 * error envelope (or with undefined) instead of rejecting, so "it resolved" is
 * not the same as "it worked". Retaining one of those would keep a screen
 * broken for the whole TTL. Callers wrapping such a service must pass a
 * predicate; the default keeps everything that resolves.
 */
export const cached = <T,>(
  key: string,
  fn: () => Promise<T>,
  ttlMs: number,
  shouldRetain: (value: T) => boolean = () => true
): Promise<T> => {
  const entry = entries.get(key);
  if (entry && entry.expiresAt > Date.now()) {
    return Promise.resolve(entry.value as T);
  }

  const startedInEpoch = epoch;

  return inflight(key, async () => {
    const value = await fn();
    if (ttlMs > 0 && startedInEpoch === epoch && shouldRetain(value)) {
      entries.set(key, {
        value,
        expiresAt:
          ttlMs === Number.POSITIVE_INFINITY
            ? Number.POSITIVE_INFINITY
            : Date.now() + ttlMs,
      });
    }
    return value;
  });
};

/**
 * Retention predicate for the `ResponseInterface` envelope most services in
 * this repo return: keep it only when the call actually succeeded.
 *
 * An empty collection is deliberately NOT retained. A 200 carrying `model: []`
 * is indistinguishable from a half-deployed backend or a migration blip, and
 * retaining one under a long TTL is not a cosmetic problem: ShipmentForm
 * intersects the services returned by payment mode and delivery type, so either
 * list arriving empty leaves zero selectable services and the cashier cannot
 * create a shipment for the rest of the session. Refetching an empty catalog is
 * cheap; being wedged is not.
 */
export const isSuccessfulResponse = (value: unknown): boolean => {
  if (!value || typeof value !== "object") return false;
  const envelope = value as { didError?: boolean; model?: unknown };
  if (envelope.didError === true || envelope.model == null) return false;
  if (Array.isArray(envelope.model) && envelope.model.length === 0) return false;
  return true;
};

/**
 * Drops retained entries. Without a prefix it clears everything, which is what
 * login and logout do. With one it clears a subtree, which is what a change of
 * business unit does: keys are built as `name|arg|arg`, so passing `name|BU123`
 * drops exactly that store's entries and leaves the rest warm.
 *
 * Bumping the epoch is what makes this safe against requests that are already
 * in flight: they still resolve for whoever is awaiting them, but their results
 * are no longer written to the cache. `pending` is cleared too, so a caller
 * arriving after the change starts a fresh request instead of joining one
 * issued under the previous store or user.
 */
export const invalidate = (prefix?: string): void => {
  epoch += 1;

  if (prefix === undefined) {
    entries.clear();
    pending.clear();
    return;
  }
  entries.forEach((_, key) => {
    if (key.startsWith(prefix)) entries.delete(key);
  });
  pending.forEach((_, key) => {
    if (key.startsWith(prefix)) pending.delete(key);
  });
};

/**
 * Builds a cache key from a service name and its arguments. Every argument
 * that changes the response must be included: a key missing the store code
 * would serve store A's configuration to store B.
 */
export const cacheKey = (
  name: string,
  ...args: Array<string | number | undefined | null>
): string => [name, ...args.map((a) => a ?? "")].join("|");

/** Test seam. Not used by application code. */
export const __resetCache = (): void => {
  entries.clear();
  pending.clear();
};
